import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RegistrationFieldConfig } from 'interfaces/subscribe.types';

import {
  DEFAULT_TOOLTIP_KEYS,
  getFieldLabel,
  getFieldTooltip,
  isAddressFieldRequired,
  isFieldDisabled,
  isFieldRequired,
  isFieldVisible,
} from '../fieldConfig.utils';

/**
 * Résout les propriétés des champs du formulaire à partir de la configuration BO.
 * Fournit des helpers shouldShow / isRequired / isDisabled / label / tooltip
 * avec fallback si fieldConfigs n'est pas fourni.
 */
export function useFieldResolver(fieldConfigs?: RegistrationFieldConfig[]) {
  const { t } = useTranslation();

  const cfgByName = useMemo(() => {
    const map: Record<string, RegistrationFieldConfig> = {};
    if (fieldConfigs) {
      fieldConfigs.forEach((c) => {
        map[c.name] = c;
      });
    }
    return map;
  }, [fieldConfigs]);

  const shouldShow = (apiName: string, fallback = true): boolean => {
    const cfg = cfgByName[apiName];
    if (!cfg) return fallback;
    return isFieldVisible(cfg);
  };

  const isRequired = (apiName: string, fallback = true): boolean => {
    const cfg = cfgByName[apiName];
    if (!cfg) return fallback;
    return isFieldRequired(cfg);
  };

  /**
   * Variante pour les champs d'adresse (pre-registration / autonomous-registration) :
   * obligatoire uniquement si display_rule === 'editable_and_mandatory'.
   */
  const isRequiredForAddress = (apiName: string, fallback = false): boolean => {
    const cfg = cfgByName[apiName];
    if (!cfg) return fallback;
    return isAddressFieldRequired(cfg);
  };

  const isDisabled = (apiName: string, fallback = false): boolean => {
    const cfg = cfgByName[apiName];
    if (!cfg) return fallback;
    return isFieldDisabled(cfg);
  };

  const label = (apiName: string, fallbackKey?: string): string => {
    const cfg = cfgByName[apiName];
    if (cfg) return getFieldLabel(cfg, t);
    return fallbackKey ? t(fallbackKey) : apiName;
  };

  const tooltip = (apiName: string): string | undefined => {
    const cfg = cfgByName[apiName];
    if (cfg) return getFieldTooltip(cfg, t);
    const key = DEFAULT_TOOLTIP_KEYS[apiName];
    return key ? t(key) : undefined;
  };

  return {
    cfgByName,
    shouldShow,
    isRequired,
    isRequiredForAddress,
    isDisabled,
    label,
    tooltip,
  };
}
